import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Button, CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import { Add, AttachFile, Delete } from '@material-ui/icons';
import React from 'react';
import Dropzone, { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';

import { hasRole } from 'shared/models/user';

import { fetchClientById, removeClientFile, uploadClientFiles } from '../actions';
import { FileDownloadLink } from '../core-ui/file-download-link';
import { CLIENT_WITH_JOBS, ClientWithJobs } from '../graphql/queries/clients';
import { useModal } from '../hooks/use-modal';
import { useReduxDispatch, useReduxState } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { readFile } from '../lib/read-file-payload';

const styles = css`
    .client-contracts-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-top: 20px;
        label {
            color: #546e7a;
            font-size: 0.75rem;
        }
        .files {
            margin-top: 6px;
            .file {
                .delete-icon {
                    visibility: hidden;
                }
                &:hover {
                    .delete-icon {
                        visibility: visible;
                    }
                }
            }
        }

        .file {
            display: flex;
            align-items: center;
            margin-top: 6px;

            .delete-icon {
                margin-left: 10px;
            }
        }

        .upload-button {
            margin-top: 6px;
            border-radius: 4px;
            border: 1px solid #ccc;
            padding: 5px 15px;
            cursor: pointer;

            &:hover {
                background-color: rgba(38, 50, 56, 0.04);
            }
            .label {
                display: flex;
                color: #263238;
                font-size: 0.875rem;
                font-weight: 500;
                align-items: center;
                text-transform: uppercase;

                .startIcon {
                    display: flex;
                    margin-left: -4px;
                    margin-right: 4px;
                }
            }
        }
    }
`;

const spinnerSize = 20;
const spinnerThickness = 2;

export const ClientContracts: React.FC<{ clientId: string }> = ({ clientId }) => {
    const { user } = useSession();
    const { data: clientData } = useQuery<{ client: ClientWithJobs }, { id: string }>(CLIENT_WITH_JOBS, {
        skip: !clientId,
        variables: { id: clientId }
    });
    const { getConfirmation } = useModal();
    const { userPermissions } = useSession();
    const dispatch = useReduxDispatch();
    const clients = useReduxState((state) => state.clients);
    const isCreating = clients.isCreating;
    const pendingRequests = useReduxState((state) => state.pendingRequests);

    React.useEffect(() => {
        if (!clients.list.get(clientId)) {
            dispatch(fetchClientById(clientId));
        }
    }, [clientId]);

    const isAM = clientData ? !!clientData.client.jobs.find((j) => j.accountManagerId === user.id) : false;

    const isUploadingContracts = () => {
        return (
            pendingRequests.has(`client-files-upload-${clientId}`) &&
            pendingRequests.get(`client-files-upload-${clientId}`).isEmpty()
        );
    };

    const handleDropContracts = (files: File[]) => {
        if (!isUploadingContracts()) {
            Promise.all(files.map(readFile)).then((result) => {
                dispatch(uploadClientFiles(clientId, result));
            });
        }
    };

    const removeContract = (key: string) => {
        dispatch(removeClientFile(clientId, key));
    };

    const handleRemoveContract = (key: string) => () => {
        getConfirmation(
            () => {
                removeContract(key);
            },
            'Are you sure you want to delete this contract?',
            'Confirm remove contract'
        );
    };

    const isBillingAdmin = hasRole(userPermissions, 'billing_admin');
    const isDisabled = isCreating || !isBillingAdmin;

    const clientContracts =
        clientId && clients.list.get(clientId)?.contracts
            ? clients.list
                  .get(clientId)
                  .contracts.filter((c) => !c.deleted)
                  .map((c) => {
                      return {
                          filename: c.key.split('/').reverse()[0],
                          key: c.key,
                          path: `media/files/${c.key}`,
                          size: c.size,
                          type: 's3Key'
                      };
                  })
            : [];

    const contractsList =
        isBillingAdmin || isAM
            ? clientContracts?.map((a) => {
                  const deleteButton = isBillingAdmin ? (
                      <Tooltip title="Remove contract">
                          <IconButton className="delete-icon" onClick={handleRemoveContract(a.key)}>
                              <Delete />
                          </IconButton>
                      </Tooltip>
                  ) : null;
                  return (
                      <div className="file" key={a.key}>
                          <FileDownloadLink path={a.path} filename={a.filename}>
                              <Button variant="outlined" startIcon={<AttachFile />}>
                                  {a.filename}
                              </Button>
                          </FileDownloadLink>
                          {deleteButton}
                      </div>
                  );
              })
            : null;

    const getDropZoneSection = (
        getRootProps: <T extends DropzoneRootProps>(props?: T) => T,
        getInputProps: <T extends DropzoneInputProps>(props?: T) => T
    ) => {
        return (
            <section className="upload-button">
                <div {...getRootProps()}>
                    <input {...getInputProps()} disabled={isDisabled || isUploadingContracts()} />
                    <span className="label">
                        <span className="startIcon">
                            <Add />
                        </span>
                        Add File
                    </span>
                </div>
            </section>
        );
    };

    const uploadZone = isUploadingContracts() ? (
        <div>
            <CircularProgress size={spinnerSize} thickness={spinnerThickness} />
        </div>
    ) : (
        <Dropzone onDrop={handleDropContracts}>
            {({ getRootProps, getInputProps }) => getDropZoneSection(getRootProps, getInputProps)}
        </Dropzone>
    );

    if (!isBillingAdmin && !isAM) return null;

    const contractsSection = (
        <div className="client-contracts-container">
            <label htmlFor="upload-button">Contract - MSA/SOW</label>
            <div className="files">{contractsList}</div>
            {uploadZone}
        </div>
    );

    return <div css={styles}>{contractsSection}</div>;
};
